import { ToastService } from "../../AppToast";
import {
  InvoiceSummaryStatusType,
  ODataPeriod,
  OInvoiceSummaryStatus,
  OPrepareSummaryStatus,
  PrepareSummaryStatus,
  SummaryDataDetails,
  type DataPeriod,
  type InvoiceSummary,
} from "../../shared/constants";
import { downloadFile } from "../../utils/downloadFile";
import { InvoiceSummaryService } from "./InvoiceSummaryService";

export const getInvoiceSummaryDateString = (
  dataPeriod: DataPeriod,
  year: number | string,
  MonthOrYear?: string | number,
  periodDate?: string
) => {
  if (
    (dataPeriod !== ODataPeriod.DAY && !MonthOrYear) ||
    (dataPeriod === ODataPeriod.DAY && !periodDate)
  ) {
    return "";
  }

  switch (dataPeriod) {
    case ODataPeriod.DAY:
      return new Date(periodDate).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
    case ODataPeriod.MONTH:
      return `tháng ${MonthOrYear} năm ${year}`;
    case ODataPeriod.QUARTER:
      return `quý ${MonthOrYear} năm ${year}`;
    default:
      return "";
  }
};

export const validateSummaryXmls = (
  summaries: InvoiceSummary | InvoiceSummary[]
) => {
  const isArray = Array.isArray(summaries);
  if (isArray && summaries.length === 0) {
    ToastService.error("Chưa lựa chọn bảng tổng hợp");
    return false;
  }
  if (isArray && summaries.length > 0) {
    let isValid = true;
    summaries.forEach((summary) => {
      if (
        (
          [
            OInvoiceSummaryStatus.WAIT_SIGN,
            OInvoiceSummaryStatus.SENT,
          ] as number[]
        ).includes(summary.status)
      ) {
        isValid = false;
      }
    });
    if (!isValid) {
      ToastService.error("Bảng tổng hợp chưa ký không tồn tại xml");
    }
    return isValid;
  }
  if (
    !isArray &&
    (
      [OInvoiceSummaryStatus.WAIT_SIGN, OInvoiceSummaryStatus.SENT] as number[]
    ).includes(summaries.status)
  ) {
    ToastService.error("Bảng tổng hợp chưa ký không tồn tại xml");
    return false;
  }
  return true;
};

export const invoiceStatusClassMap = (status: InvoiceSummaryStatusType) => {
  switch (status) {
    case OInvoiceSummaryStatus.WAIT_SIGN:
    case OInvoiceSummaryStatus.WAIT_APPROVE:
    case OInvoiceSummaryStatus.SENT:
      return "2";
    case OInvoiceSummaryStatus.SIGNED:
      return "4";
    case OInvoiceSummaryStatus.SUCCESSFUL:
      return "0";
    default:
      return "1";
  }
};
export const prepareInvoiceStatusClassMap = (status: PrepareSummaryStatus) => {
  switch (status) {
    case OPrepareSummaryStatus.ORIGIN:
    case OPrepareSummaryStatus.ADJUSTING:
    case OPrepareSummaryStatus.REPLACING:
    case OPrepareSummaryStatus.EXPLAINING:
    case OPrepareSummaryStatus.NOTIFYING:
      return "2";
    case OPrepareSummaryStatus.REPLACED:
    case OPrepareSummaryStatus.ADJUSTED:
      return "4";
    default:
      return "1";
  }
};

export const preInvoiceStatusClassMap = (status: InvoiceSummaryStatusType) => {
  switch (status) {
  }
};

export const convertDataPeriod = (dataPeriod: string) => {
  switch (dataPeriod) {
    case ODataPeriod.DAY:
      return "Ngày";
    case ODataPeriod.MONTH:
      return "Tháng";
    case ODataPeriod.QUARTER:
      return "Quý";
  }
};

export const downloadXml = (items: InvoiceSummary[] | InvoiceSummary) => {
  const isValid = validateSummaryXmls(items);

  if (!isValid) return;
  const isArray = Array.isArray(items);
  if (isArray) {
    items.forEach((item) => {
      InvoiceSummaryService.getXml(item.id).then((res) => {
        downloadFile(res, `bang-tong-hop.xml`);
      });
    });
  } else if (!isArray) {
    InvoiceSummaryService.getXml(items.id).then((res) => {
      downloadFile(res, `bang-tong-hop.xml`);
    });
  }
};
export const exportExcel = (items: InvoiceSummary[] | InvoiceSummary) => {
  const isArray = Array.isArray(items);
  if (isArray) {
    items.forEach((item) => {
      InvoiceSummaryService.exportExcel(item.id).then((res) => {
        downloadFile(res, `bang-tong-hop.xlsx`);
      });
    });
  } else if (!isArray) {
    InvoiceSummaryService.exportExcel(items.id).then((res) => {
      downloadFile(res, `bang-tong-hop.xlsx`);
    });
  }
};

export const combineSummaryDetails = (
  currDetails: SummaryDataDetails[],
  addedDetails: SummaryDataDetails[]
) => {
  // Combine arrays and remove duplicates based on ID
  const combinedDetails = [...currDetails];

  addedDetails.forEach((detail) => {
    if (
      !combinedDetails.some(
        (existing) => existing.invoiceDetailId === detail.invoiceDetailId
      )
    ) {
      combinedDetails.push(detail);
    }
  });

  return combinedDetails;
};

export const formatVatRate = (vatRate: string | null) => {
  if (!vatRate) return "";
  // Check if the value is a percentage (contains a number with comma as decimal separator)
  const percentageRegex = /^(\d+)[,](\d+)$/;

  if (percentageRegex.test(vatRate)) {
    // Extract the number parts before and after the comma
    const match = vatRate.match(percentageRegex);
    if (match) {
      const wholeNumber = match[1];
      const fraction = match[2];

      // Check if the fraction part is all zeros
      if (/^0+$/.test(fraction)) {
        return `${wholeNumber}%`;
      } else {
        // Convert the fraction to a decimal format
        // For example, '250000' should become '.25'
        const decimalPart = parseFloat(`0.${fraction}`).toFixed(2).substring(1);
        return `${wholeNumber}${decimalPart}%`;
      }
    }
  }
  if (!isNaN(Number(vatRate))) {
    return `${vatRate}%`;
  }

  return vatRate;
};

export const getMQDropdownValues = (dataPeriod: DataPeriod) => {
  switch (dataPeriod) {
    case ODataPeriod.MONTH:
      return [
        { value: 1, label: "1" },
        { value: 2, label: "2" },
        { value: 3, label: "3" },
        { value: 4, label: "4" },
        { value: 5, label: "5" },
        { value: 6, label: "6" },
        { value: 7, label: "7" },
        { value: 8, label: "8" },
        { value: 9, label: "9" },
        { value: 10, label: "10" },
        { value: 11, label: "11" },
        { value: 12, label: "12" },
      ];
    case ODataPeriod.QUARTER:
      return [
        { value: 1, label: "1" },
        { value: 2, label: "2" },
        { value: 3, label: "3" },
        { value: 4, label: "4" },
      ];
    default:
      return [];
  }
};

export const getSummaryErrorMessage = (res: {
  data: {
    error: { code: number; message: string; details: string };
  };
}) => {
  if (res?.data?.error?.message) return res.data.error.message;
  return "Có lỗi xảy ra. Vui lòng kiểm tra lại nghiệp vụ";
};

export const validateSummaryDetailsFromExcel = (
  summaryDetails: SummaryDataDetails[],
  excelData: SummaryDataDetails[]
) => {
  for (const excelItem of excelData) {
    const isDuplicate = summaryDetails.some(
      (summaryItem) =>
        summaryItem.invoiceSerial === excelItem.invoiceSerial &&
        summaryItem.invoiceNumber === excelItem.invoiceNumber
    );
    if (isDuplicate) {
      ToastService.error(
        `Đã tồn tại hóa đơn số ${excelItem.invoiceSerial} - ${excelItem.invoiceNumber}`
      );
      return false;
    }
  }
  return true;
};
